import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Minus, Plus, Check, ChevronLeft, Truck, Shield, Star, MessageCircle, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { usePincode } from '../contexts/PincodeContext';
import { t, getProductName, getProductDescription } from '../lib/translations';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { addToCart, cart, updateQuantity } = useCart();
  const { currentPincode } = usePincode();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState({ average_rating: 0, total_reviews: 0, rating_breakdown: {} });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  const cartItem = cart.items.find(item => item.product_id === id);
  const quantity = cartItem?.quantity || 0;

  const currentPrice = product?.price;
  const currentDiscountPrice = product?.discount_price;
  const currentStock = product?.stock;

  // Build deduplicated image list: main image first, then extra images (skip duplicates)
  const allImages = product
    ? [product.image, ...(product.images || [])]
        .filter(Boolean)
        .filter((img, idx, arr) => arr.indexOf(img) === idx)   // deduplicate
    : [];

  // Reset selected image when product changes
  useEffect(() => {
    setSelectedImageIndex(0);
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, reviewsRes, summaryRes] = await Promise.all([
          axios.get(`${API}/products/${id}`),
          axios.get(`${API}/products/${id}/reviews`),
          axios.get(`${API}/products/${id}/reviews/summary`)
        ]);
        setProduct(productRes.data);
        setReviews(reviewsRes.data);
        setReviewSummary(summaryRes.data);
      } catch (error) {
        console.error('Failed to fetch product:', error);
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const formatPrice = (price) => `₹${price.toFixed(0)}`;
  
  const handleAddToCart = async () => {
    setAdding(true);
    const success = await addToCart(product.id, 1);
    setAdding(false);
    if (success) {
      setAdded(true);
      setTimeout(() => setAdded(false), 1500);
    }
  };

  const handleSubmitReview = async () => {
    if (!newReview.comment.trim()) {
      toast.error('Please write a review');
      return;
    }
    
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/products/${id}/reviews`, {
        product_id: id,
        rating: newReview.rating,
        comment: newReview.comment
      });
      toast.success('Review submitted successfully!');
      setShowReviewForm(false);
      setNewReview({ rating: 5, comment: '' });
      
      // Refresh reviews
      const [reviewsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/products/${id}/reviews`),
        axios.get(`${API}/products/${id}/reviews/summary`)
      ]);
      setReviews(reviewsRes.data);
      setReviewSummary(summaryRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: getProductName(product),
      text: `Check out ${getProductName(product)} on AV Mart Shamli`,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const handleWhatsAppEnquiry = () => {
    const message = `Hi, I'm interested in ${getProductName(product)} (₹${currentDiscountPrice || currentPrice}). ${window.location.href}`;
    window.open(`https://wa.me/917251972000?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!product) return null;

  const hasDiscount = currentDiscountPrice && currentDiscountPrice < currentPrice;
  const discountPercent = hasDiscount 
    ? Math.round(((currentPrice - currentDiscountPrice) / currentPrice) * 100) 
    : 0;
  const canDeliver = product.deliverable_pincodes?.includes(currentPincode);

  return (
    <div className="min-h-screen bg-slate-50 py-4 sm:py-8" data-testid="product-detail-page">
      <div className="container-app">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:text-green-600 mb-4 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100">
              <div className="aspect-square relative">
                <img
                  src={allImages[selectedImageIndex] || product.image}
                  alt={getProductName(product)}
                  className="w-full h-full object-cover transition-opacity duration-200"
                  key={allImages[selectedImageIndex]}  // force re-render on image change
                />
                
                {hasDiscount && (
                  <Badge className="absolute top-4 left-4 bg-orange-500 text-white text-lg px-3 py-1">
                    -{discountPercent}%
                  </Badge>
                )}
                
                {currentStock === 0 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white font-bold text-xl bg-red-500 px-6 py-2 rounded-full">
                      {t('outOfStock')}
                    </span>
                  </div>
                )}

                {/* Image counter badge */}
                {allImages.length > 1 && (
                  <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                    {selectedImageIndex + 1} / {allImages.length}
                  </div>
                )}
              </div>
            </div>

            {/* Thumbnails — shown whenever there are 2+ images */}
            {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImageIndex === idx
                        ? 'border-green-600 ring-2 ring-green-200'
                        : 'border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                {getProductName(product)}
              </h1>
              <p className="text-slate-500 mt-1">{product.unit}</p>
              
              {/* Rating Summary */}
              {reviewSummary.total_reviews > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="font-medium">{reviewSummary.average_rating.toFixed(1)}</span>
                  </div>
                  <span className="text-slate-500 text-sm">
                    ({reviewSummary.total_reviews} reviews)
                  </span>
                </div>
              )}
            </div>

            {/* Price */}
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold text-green-600">
                {formatPrice(hasDiscount ? currentDiscountPrice : currentPrice)}
              </span>
              {hasDiscount && (
                <span className="text-xl text-slate-400 line-through">
                  {formatPrice(currentPrice)}
                </span>
              )}
            </div>

            {/* Description */}
            <div className="prose prose-sm text-slate-600">
              <p>{getProductDescription(product)}</p>
            </div>

            {/* Delivery Info */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Truck className={`w-5 h-5 ${canDeliver ? 'text-green-600' : 'text-red-500'}`} />
                <span className={canDeliver ? 'text-green-600' : 'text-red-500'}>
                  {canDeliver 
                    ? `Delivery available to ${currentPincode}`
                    : `Delivery not available to ${currentPincode}`
                  }
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-green-600" />
                <span className="text-slate-600">
                  {product.is_returnable ? 'Easy returns available' : 'Non-returnable'}
                </span>
              </div>
            </div>

            {/* Stock */}
            {currentStock > 0 && currentStock <= 10 && (
              <Badge variant="outline" className="text-orange-600 border-orange-300">
                Only {currentStock} left in stock
              </Badge>
            )}

            {/* Add to Cart */}
            {currentStock > 0 && canDeliver && (
              <div className="flex items-center gap-4">
                {quantity > 0 ? (
                  <div className="flex items-center gap-3 bg-green-50 rounded-full px-4 py-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => updateQuantity(product.id, quantity - 1)}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="w-8 text-center font-medium text-lg">{quantity}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => updateQuantity(product.id, quantity + 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="lg"
                    className={`flex-1 h-12 text-lg btn-press ${
                      added ? 'bg-green-500' : 'bg-green-600 hover:bg-green-700'
                    }`}
                    onClick={handleAddToCart}
                    disabled={adding}
                    data-testid="add-to-cart-btn"
                  >
                    {added ? (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Added!
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        {t('addToCart')}
                      </>
                    )}
                  </Button>
                )}
                
                {/* Action Buttons */}
                <Button variant="outline" size="icon" className="h-12 w-12" onClick={handleShare}>
                  <Share2 className="w-5 h-5" />
                </Button>
                <Button variant="outline" size="icon" className="h-12 w-12 text-green-600" onClick={handleWhatsAppEnquiry}>
                  <MessageCircle className="w-5 h-5" />
                </Button>
              </div>
            )}

            {!canDeliver && (
              <div className="bg-red-50 text-red-600 rounded-xl p-4 text-center">
                Sorry, this product cannot be delivered to your pincode
              </div>
            )}
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">
              Customer Reviews ({reviewSummary.total_reviews})
            </h2>
            {isAuthenticated && !showReviewForm && (
              <Button onClick={() => setShowReviewForm(true)} className="bg-green-600 hover:bg-green-700">
                Write a Review
              </Button>
            )}
          </div>

          {/* Rating Summary */}
          {reviewSummary.total_reviews > 0 && (
            <div className="flex items-center gap-6 mb-6 p-4 bg-slate-50 rounded-xl">
              <div className="text-center">
                <div className="text-4xl font-bold text-slate-900">{reviewSummary.average_rating.toFixed(1)}</div>
                <div className="flex items-center justify-center gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-4 h-4 ${
                        star <= Math.round(reviewSummary.average_rating)
                          ? 'text-yellow-400 fill-current'
                          : 'text-slate-300'
                      }`}
                    />
                  ))}
                </div>
                <div className="text-sm text-slate-500 mt-1">{reviewSummary.total_reviews} reviews</div>
              </div>
              <div className="flex-1 space-y-1">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = reviewSummary.rating_breakdown[rating] || 0;
                  const percent = reviewSummary.total_reviews > 0 
                    ? (count / reviewSummary.total_reviews) * 100 
                    : 0;
                  return (
                    <div key={rating} className="flex items-center gap-2 text-sm">
                      <span className="w-3">{rating}</span>
                      <Star className="w-3 h-3 text-yellow-400 fill-current" />
                      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-yellow-400 transition-all"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="w-8 text-slate-500">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Review Form */}
          {showReviewForm && (
            <div className="border border-slate-200 rounded-xl p-4 mb-6">
              <h3 className="font-medium mb-3">Write Your Review</h3>
              <div className="space-y-4">
                <div>
                  <Label>Rating</Label>
                  <div className="flex items-center gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setNewReview({ ...newReview, rating: star })}
                        className="p-1"
                      >
                        <Star
                          className={`w-6 h-6 transition-colors ${
                            star <= newReview.rating
                              ? 'text-yellow-400 fill-current'
                              : 'text-slate-300 hover:text-yellow-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Your Review</Label>
                  <Textarea
                    value={newReview.comment}
                    onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                    placeholder="Share your experience with this product..."
                    rows={3}
                    className="mt-1"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowReviewForm(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSubmitReview} 
                    disabled={submittingReview}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Reviews List */}
          <div className="space-y-4">
            {reviews.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No reviews yet. Be the first to review!
              </div>
            ) : (
              reviews.map((review) => (
                <div key={review.id} className="border-b border-slate-100 pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 font-medium text-sm">
                          {review.user_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="font-medium text-slate-900">{review.user_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${
                            star <= review.rating ? 'text-yellow-400 fill-current' : 'text-slate-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-slate-600">{review.comment}</p>
                  <p className="text-xs text-slate-400 mt-2">
                    {new Date(review.created_at).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
